import { createFileRoute } from "@tanstack/react-router";
import { format, parse } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useTeacherClassesGetTeacherClassDetail } from "@/orval/teacher-classes";

const timeParseBaseDate = new Date(0);
const blockedRosterTitle = "Roster locked until registration closes";

function formatClassTime(time: string) {
  return format(parse(time, "HH:mm:ss", timeParseBaseDate), "h:mm a");
}

export const Route = createFileRoute("/_app/organizations/$organizationId/teacher/classes_/$classId/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { organizationId, classId } = Route.useParams();
  const { data: classDetail, isLoading } = useTeacherClassesGetTeacherClassDetail(organizationId, classId);

  if (isLoading) {
    return <Skeleton className="h-32 w-full" />;
  }

  if (!classDetail) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Details</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid min-w-0 grid-cols-1 gap-4 text-sm sm:grid-cols-2 md:grid-cols-4">
          <div>
            <p className="text-muted-foreground">Semester</p>
            <p className="font-medium">{classDetail.semesterName}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Day</p>
            <p className="font-medium">{classDetail.classDayName}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Time</p>
            <p className="font-medium">
              {formatClassTime(classDetail.startTime)} - {formatClassTime(classDetail.endTime)}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground">Enrolled</p>
            <p className="font-medium">
              {classDetail.enrolledStudentsCount}
              {classDetail.studentLimit != null ? ` / ${classDetail.studentLimit}` : ""}
            </p>
          </div>
        </div>
        {classDetail.isRosterAccessBlocked ? (
          <Badge variant="secondary" className="mt-4">
            {blockedRosterTitle}
          </Badge>
        ) : null}
        {classDetail.description ? (
          <div className="mt-4 text-sm">
            <p className="text-muted-foreground">Description</p>
            <p className="font-medium text-foreground">{classDetail.description}</p>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
